import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ProductionProcessDto } from 'src/app/shared/dto/production-process.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ProductionProcessesService } from '../../services/production-processes.service';
import { CommonModule } from '@angular/common';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-production-processes',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzTableModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzCheckboxModule,
        CommonModule,
        NzSelectModule,
        NzPageHeaderModule,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzDropDownModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './production-processes.component.html',
    styleUrl: './production-processes.component.scss',
})

export class ProductionProcessesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    productionProcesses: ProductionProcessDto[] = [];
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    selectedProcess: ProductionProcessDto = new ProductionProcessDto();
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        qualityControl: FormControl<boolean>;
    }>;

    private readonly productionProcessService = inject(ProductionProcessesService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('productionProcesses', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            qualityControl: [false, [Validators.required]],
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        try {
            this.currentUser = this.userLoggedService.userLogged;
            this.innerWidth = window.innerWidth;

            await this.productionProcessService.setupWebSocket();
            this.productionProcessService.productionProcessess$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: (processes) => {
                        this.productionProcesses = processes;
                        this.selectedProcess = this.productionProcesses.find(pp => pp.id === this.selectedProcess.id) ?? new ProductionProcessDto();
                    },
                    error: (e) => {
                        this.message.create('Error cargando procesos de producción', e.message);
                    }
                });

            this.isLoading = false;
        } catch (e: any) {
            this.message.create('error', e.message);
        }
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.productionProcessService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.selectedProcess = new ProductionProcessDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo proceso de producción';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.setData();
        this.openDrawer();
    }

    view(data: ProductionProcessDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.selectedProcess = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo proceso ' + this.selectedProcess?.name;
        this.fieldsReadOnly = true;

        this.setData();
        this.openDrawer();
    }

    edit(data: ProductionProcessDto) {
        this.selectedProcess = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando proceso ' + this.selectedProcess?.name;
        this.fieldsReadOnly = false;

        this.setData();
        this.openDrawer();
    }

    setData() {

    }

    cancelEditing() {
        this.closeDrawer();
    }

    async delete(processId: number) {
        try {
            if (window.confirm('¿Estás seguro de que quieres eliminar este proceso de producción? Esta acción no se puede deshacer.')) {
                const deletedProcess: ProductionProcessDto = await this.productionProcessService.deleteProcess(processId);

                const index = this.productionProcesses.findIndex((p) => p.id === processId);

                if (index < 0) {
                    this.message.create('error', `No se ha podido eliminar el proceso ${deletedProcess.name} con id ${processId}, proceso de producción no encontrado`);

                    return;
                }

                this.productionProcesses.splice(index, 1);
                const data = [...this.productionProcesses];
                this.productionProcesses = data;

                this.message.create('success', 'Proceso de producción eliminado correctamente');
            }
        } catch (e: any) {
            this.message.create('error', `${e.message}`);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.selectedProcess = new ProductionProcessDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const processToBeProcessed: ProductionProcessDto = {
                id: this.selectedProcess.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                qualityControl: this.validateForm.controls.qualityControl.value ? this.validateForm.controls.qualityControl.value : false,
                createdBy: this.drawerMode === DrawerMode.CREATE ? this.currentUser.id : this.selectedProcess.createdBy
            };

            if (!this.isFormValid(processToBeProcessed)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                const createdProcess: ProductionProcessDto = await this.productionProcessService.createProcess(processToBeProcessed);

                this.productionProcesses.push(createdProcess);
                const data = [...this.productionProcesses];
                this.productionProcesses = data;

                this.isSaving = false;
                this.message.create('success', 'Proceso de producción creada correctamente');
            } else {
                const updatedProcess: ProductionProcessDto = await this.productionProcessService.updateProcess(processToBeProcessed);

                const index = this.productionProcesses.findIndex((r) => r.id === updatedProcess.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar el proceso ${processToBeProcessed.name} con id ${processToBeProcessed.id}, proceso de producción no encontrado`);

                    return;
                }

                this.productionProcesses[index] = updatedProcess;
                const data = [...this.productionProcesses];
                this.productionProcesses = data;

                this.isSaving = false;
                this.message.create('success', 'Proceso de producción actualizado correctamente');
            }

            this.closeDrawer();
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }

    isFormValid(productionProcess: ProductionProcessDto): boolean {
        if (!productionProcess.name) {
            this.message.create('error', 'El nombre del proceso de producción es obligatorio');

            return false;
        }

        return true;
    }
}
